/**
 * ExportDataModal Component
 * 
 * Modal for selecting data export format.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Format selection (JSON/CSV)
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see specs/004-settings-profile-png/spec.md
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, FileJson, FileSpreadsheet } from 'lucide-react';
import { ExportDataOptions } from '../../../types/profile.types';

interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (options: ExportDataOptions) => void;
  loading?: boolean;
}

export function ExportDataModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  loading = false
}: ExportDataModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const [selectedFormat, setSelectedFormat] = useState<'json' | 'csv'>('json');

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedFormat('json');
    }
  }, [isOpen]);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ format: selectedFormat });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-data-title"
      aria-describedby="export-data-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="export-data-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Export Data
              </h2>
              <p id="export-data-description" className="text-sm text-gray-600 dark:text-gray-400">
                Select the format for your data export
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="space-y-4">
              {/* JSON Option */}
              <label
                htmlFor="format-json"
                className={`flex cursor-pointer items-center space-x-3 rounded-lg border-2 p-4 transition-colors ${
                  selectedFormat === 'json'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <input
                  id="format-json"
                  name="export-format"
                  type="radio"
                  value="json"
                  checked={selectedFormat === 'json'}
                  onChange={() => setSelectedFormat('json')}
                  className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <FileJson className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    JSON (Structured data)
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Suitable for programmatic access and data migration
                  </div>
                </div>
              </label>

              {/* CSV Option */}
              <label
                htmlFor="format-csv"
                className={`flex cursor-pointer items-center space-x-3 rounded-lg border-2 p-4 transition-colors ${
                  selectedFormat === 'csv'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <input
                  id="format-csv"
                  name="export-format"
                  type="radio"
                  value="csv"
                  checked={selectedFormat === 'csv'}
                  onChange={() => setSelectedFormat('csv')}
                  className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <FileSpreadsheet className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    CSV (Spreadsheet compatible)
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Suitable for viewing in Excel or similar tools
                  </div>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                type="button" 
                onClick={onClose} 
                className="btn-secondary btn-md"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-primary btn-md"
                disabled={loading}
                data-loading={loading ? 'true' : undefined}
              >
                {loading ? 'Exporting...' : 'Export Data'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

